import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, AlertCircle } from 'lucide-react';
import { PraticaService } from "../services/apiService";

// Import Componenti Step
import { Step1_DocumentoCliente } from '../components/Step1_DocumentoCliente';
import { Step2_DatiPratica } from '../components/Step2_DatiPratica';
import { Step3_ContattiDelegati } from '../components/Step3_ContattiDelegati';
import { Step4_SoggettoInvestigato } from '../components/Step4_SoggettoInvestigato';
import { Step_Economico } from '../components/Step_Economico';

export const StoricoPraticaView: React.FC = () => {
    const navigate = useNavigate();
    const { id, versionId } = useParams();

    const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1);
    const [snapshot, setSnapshot] = useState<any>(null);
    const [versionDate, setVersionDate] = useState<string>('');
    const [loading, setLoading] = useState(true);

    // Stato minimo per riusare i componenti Step in sola lettura
    const [selectedClientId, setSelectedClientId] = useState<string | number>('');
    const [clientsList, setClientsList] = useState<any[]>([]);
    const [clientMode, setClientMode] = useState<'existing' | 'new'>('existing');
    const [selectedModelId, setSelectedModelId] = useState<string>('');
    const [selectedMandatoCode, setSelectedMandatoCode] = useState<string>('');
    const [dynamicFields, setDynamicFields] = useState<any[]>([]);
    const [delegati, setDelegati] = useState<any[]>([]);
    const [formValues, setFormValues] = useState<Record<string, any>>({});

    useEffect(() => {
        const fetchHistory = async () => {
            try {
                const res = await PraticaService.getHistory(Number(id));
                const historyArray = Array.isArray(res) ? res : (res.data || []);
                const version = historyArray.find((h: any) => String(h.versionId) === String(versionId));

                if (version) {
                    const snap = version.snapshot;
                    setSnapshot(snap);
                    setVersionDate(version.versionDate);

                    const dati = snap.datiAggiuntivi || {};
                    setFormValues(dati);

                    // Il nome cliente si ricava dal titolo pratica ("Pratica RIF - Nome Cliente")
                    const nameParts = snap.name.split(' - ');
                    const extractedClientName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : 'Cliente Storico';

                    const fakeId = 999999;
                    setSelectedClientId(fakeId);

                    // Cliente fittizio con nome e ragioneSociale, così lo Step 1 lo visualizza
                    setClientsList([{
                        id: fakeId,
                        nome: extractedClientName,
                        surname: '',
                        ragioneSociale: extractedClientName,
                        tipo: dati.soggetto_tipo || 'Privato',
                        email: dati.email || '',
                        indirizzo: dati.indirizzo_residenza || '',
                        citta: dati.citta || ''
                    } as any]);

                    // Campi dinamici necessari perché lo Step 2 venga renderizzato
                    setDynamicFields([
                        { key: 'rif_numero_pratica', label: 'Nr. Mandato', type: 'text', required: true, description: '' },
                        { key: 'codice_mandato', label: 'Oggetto', type: 'select-mandato', required: true, description: '' },
                        { key: 'oggetto_mandato_text', label: 'Testo Oggetto', type: 'textarea', required: true, description: '' },
                        { key: 'elementi_fatto', label: 'Elementi Fatto', type: 'select-elementi-fatto', required: true, description: '' }
                    ]);

                    if (dati.selectedModelId) setSelectedModelId(dati.selectedModelId);
                    if (dati.codice_mandato) setSelectedMandatoCode(dati.codice_mandato);
                    if (snap.delegati) setDelegati(snap.delegati);
                }
            } catch (err) {
                console.error("Errore caricamento storico:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchHistory();
    }, [id, versionId]);

    if (loading) return <div className="h-screen flex items-center justify-center font-medium text-gray-500">Recupero dati storici...</div>;
    if (!snapshot) return <div className="p-8 text-center text-red-500">Impossibile trovare la versione selezionata.</div>;

    // All'ultimo step "Chiudi" torna indietro
    const handleNext = () => {
        if (step < 5) {
            setStep(prev => (prev + 1) as any);
        } else {
            navigate(`/pratiche/${id}`);
        }
    };

    const handleBack = () => {
        if (step > 1) setStep(prev => (prev - 1) as any);
        else navigate(`/pratiche/${id}`);
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50/50">
            <div className="bg-amber-100 border-b border-amber-200 p-3 flex justify-center items-center gap-2 text-amber-800 shadow-sm sticky top-0 z-50">
                <AlertCircle size={20} className="shrink-0" />
                <span className="font-medium text-sm text-center">
                    <strong>SOLA LETTURA:</strong> Stai visualizzando i dati congelati al {new Date(versionDate).toLocaleString('it-IT')}.
                </span>
            </div>

            <div className="p-4 sm:p-6 max-w-5xl mx-auto w-full flex-1">
                <div className="mb-6 flex justify-between items-end">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Archivio Storico Pratica</h1>
                        <p className="text-sm text-gray-500 font-medium">Visualizzazione Step {step} di 5</p>
                    </div>
                </div>

                <div className="pointer-events-none opacity-95 select-none transition-all duration-300">
                    {step === 1 && (
                        <Step1_DocumentoCliente
                            selectedModelId={selectedModelId}
                            setSelectedModelId={() => {}}
                            selectedAmbito=""
                            setSelectedAmbito={() => {}}
                            clientMode={clientMode}
                            setClientMode={() => {}}
                            selectedClientId={selectedClientId}
                            setSelectedClientId={() => {}}
                            clientsList={clientsList}
                            searchQuery=""
                            setSearchQuery={() => {}}
                            newClientData={{}}
                            setNewClientData={() => {}}
                            isLoadingClients={false}
                            currentPage={1}
                            setCurrentPage={() => {}}
                            clientFiles={[]}
                            setClientFiles={() => {}}
                            bypassDoc={true}
                            setBypassDoc={() => {}}
                            lockCoreFields={true}
                            disabled={false}
                        />
                    )}
                    {step === 2 && (
                        <Step2_DatiPratica
                            dynamicFields={dynamicFields}
                            formValues={formValues}
                            setFormValues={() => {}}
                            selectedMandatoCode={selectedMandatoCode}
                            setSelectedMandatoCode={() => {}}
                            selectedModelId={selectedModelId}
                            lockCoreFields={true}
                        />
                    )}
                    {step === 3 && <Step3_ContattiDelegati formValues={formValues} setFormValues={() => {}} delegati={delegati} setDelegati={() => {}} selectedModelId={selectedModelId} />}
                    {step === 4 && <Step_Economico formValues={formValues} setFormValues={() => {}} />}
                    {step === 5 && <Step4_SoggettoInvestigato formValues={formValues} setFormValues={() => {}} files={[]} setFiles={() => {}} />}
                </div>
            </div>

            <div className="sticky bottom-0 w-full bg-white/90 backdrop-blur-md border-t border-gray-200 p-4 sm:px-8 flex justify-between items-center z-50 shadow-lg">
                <button onClick={handleBack} className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-bold transition-all">
                    {step === 1 ? "Esci" : "Indietro"}
                </button>
                <button onClick={handleNext} className="bg-blue-600 text-white px-10 py-2.5 rounded-xl flex items-center gap-2 hover:bg-blue-700 font-bold shadow-md transition-all active:scale-95">
                    {step === 5 ? "Chiudi" : "Successivo"} <ArrowRight size={20}/>
                </button>
            </div>
        </div>
    );
};

export default StoricoPraticaView;